import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DIAM_API_PATH,
  DIAM_TEAM_ID,
  updateIdentityStatus,
  updateTeamMembershipSuspension,
} from '../../diamApi';
import { useApiEssentials } from '../../../../shared/hooks/useApiEssentials';
import {
  IDENTITY_STATUS,
  IdentityDisplayStatus,
} from '../IdentitiesGrid/types';

const SUSPENSION_REASON = 'Updated from DIAM portal';

function invalidateIdentityQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  identityId: string,
) {
  queryClient.invalidateQueries({ queryKey: ['diam', 'identities'] });
  queryClient.invalidateQueries({
    queryKey: ['diam', 'teams', DIAM_TEAM_ID, 'members', identityId],
  });
}

export function useUpdateIdentityAccess(identityId: string) {
  const { getIdToken, apiUrl } = useApiEssentials(DIAM_API_PATH);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      currentStatus,
      nextStatus,
    }: {
      currentStatus: IdentityDisplayStatus;
      nextStatus: IdentityDisplayStatus;
    }) => {
      const idToken = await getIdToken();

      if (
        currentStatus === IDENTITY_STATUS.ACTIVE &&
        nextStatus === IDENTITY_STATUS.DEACTIVATED
      ) {
        return updateIdentityStatus(apiUrl, idToken, identityId, false);
      }

      if (
        currentStatus === IDENTITY_STATUS.DEACTIVATED &&
        nextStatus === IDENTITY_STATUS.ACTIVE
      ) {
        return updateIdentityStatus(apiUrl, idToken, identityId, true);
      }

      if (
        currentStatus === IDENTITY_STATUS.ACTIVE &&
        nextStatus === IDENTITY_STATUS.SUSPENDED
      ) {
        return updateTeamMembershipSuspension(
          apiUrl,
          idToken,
          identityId,
          true,
          SUSPENSION_REASON,
        );
      }

      if (
        currentStatus === IDENTITY_STATUS.SUSPENDED &&
        nextStatus === IDENTITY_STATUS.ACTIVE
      ) {
        return updateTeamMembershipSuspension(
          apiUrl,
          idToken,
          identityId,
          false,
          SUSPENSION_REASON,
        );
      }

      throw new Error('Unsupported identity status transition');
    },
    onSettled: () => {
      invalidateIdentityQueries(queryClient, identityId);
    },
  });
}
